import React, { useState } from 'react';
import { Button } from './button';
import { Menu, X, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export function CapsuleNavbar({ onLoginClick }: { onLoginClick?: () => void }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const navItems = [
    { name: 'Features', href: '#features' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Testimonials', href: '#testimonials' },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // For internal section links, scroll to section
    if (href.startsWith('#')) {
      e.preventDefault();
      const targetElement = document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
      setIsMenuOpen(false);
    } else {
      // For page links, let react-router handle it
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="w-full z-50 fixed top-0 left-0 px-4 py-3">
      <div className="mx-auto max-w-7xl">
        {/* Desktop Navbar */}
        <div className="flex items-center justify-between w-full h-14 rounded-xl bg-background/80 backdrop-blur-lg border border-border/60 shadow-sm px-6 transition-all duration-300">
          {/* Left: Logo */}
          <div className="flex items-center">
            <div className="font-bold text-xl font-serif">Caude</div>
          </div>

          {/* Center: Navigation Links (Desktop) */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-200 cursor-pointer font-sans"
              >
                {item.name}
              </a>
            ))}
          </div>

          {/* Right: Auth Buttons */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              // Show profile button when authenticated
              <Button 
                variant="ghost" 
                size="sm" 
                className="hidden md:inline-flex"
                asChild
              >
                <Link to="/profile">
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </Link>
              </Button>
            ) : (
              // Show sign in button when not authenticated
              <Button 
                variant="ghost" 
                size="sm" 
                className="hidden md:inline-flex"
                onClick={onLoginClick}
              >
                Sign In
              </Button>
            )}
            
            {/* Download Button */}
            <Button 
              variant="default" 
              size="sm" 
              className="hidden md:inline-flex"
              asChild
            >
              <Link to="/download">
                Download
              </Link>
            </Button>
            
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-2 rounded-xl bg-background/90 backdrop-blur-lg border border-border/60 shadow-lg p-4 transition-all duration-300">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="text-sm font-medium text-foreground/80 hover:text-foreground py-2 px-4 rounded-lg hover:bg-accent transition-colors duration-200 cursor-pointer font-sans"
                >
                  {item.name}
                </a>
              ))}
              <div className="flex flex-col space-y-3 pt-2 border-t border-border/60">
                {isAuthenticated ? (
                  // Show profile option when authenticated
                  <Button 
                    variant="outline" 
                    className="w-full"
                    asChild
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Link to="/profile">
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Link>
                  </Button>
                ) : (
                  // Show login option when not authenticated
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      onLoginClick?.();
                      setIsMenuOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                )}
                <Button 
                  variant="default" 
                  className="w-full"
                  asChild
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Link to="/download">
                    Download
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}